import React from 'react';
import { clsx } from '../../utils/clsx';
import './Table.css';

const BaseCell = React.forwardRef(function BaseCell(
  { as: Component, header = false, align = 'left', className, children, scope, ...rest },
  ref,
) {
  const Tag = Component || (header ? 'th' : 'td');
  const finalScope = header && !scope ? 'col' : scope;

  return (
    <Tag
      ref={ref}
      scope={header ? finalScope : undefined}
      className={clsx(
        'ds-table__cell',
        header && 'ds-table__cell--header',
        align !== 'left' && `ds-table__cell--align-${align}`,
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
});

const TableCell = React.forwardRef(function TableCell(props, ref) {
  return <BaseCell ref={ref} {...props} />;
});

const TableHeaderCell = React.forwardRef(function TableHeaderCell(props, ref) {
  return <BaseCell ref={ref} header {...props} />;
});

const TableRow = React.forwardRef(function TableRow(
  { children, className, selected = false, clickable = false, ...rest },
  ref,
) {
  return (
    <tr
      ref={ref}
      className={clsx(
        'ds-table__row',
        selected && 'ds-table__row--selected',
        clickable && 'ds-table__row--clickable',
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
});

const TableHead = React.forwardRef(function TableHead({ children, className, ...rest }, ref) {
  return (
    <thead ref={ref} className={clsx('ds-table__head', className)} {...rest}>
      {children}
    </thead>
  );
});

const TableBody = React.forwardRef(function TableBody({ children, className, ...rest }, ref) {
  return (
    <tbody ref={ref} className={clsx('ds-table__body', className)} {...rest}>
      {children}
    </tbody>
  );
});

const TableFooter = React.forwardRef(function TableFooter({ children, className, ...rest }, ref) {
  return (
    <tfoot ref={ref} className={clsx('ds-table__footer', className)} {...rest}>
      {children}
    </tfoot>
  );
});

const TableCaption = React.forwardRef(function TableCaption(
  { children, className, position = 'bottom', ...rest },
  ref,
) {
  return (
    <caption
      ref={ref}
      className={clsx('ds-table__caption', position === 'top' && 'ds-table__caption--top', className)}
      {...rest}
    >
      {children}
    </caption>
  );
});

const TableContainer = React.forwardRef(function TableContainer(
  {
    children,
    className,
    as: Component = 'div',
    scrollX = true,
    bordered = true,
    elevated = false,
    ...rest
  },
  ref,
) {
  return (
    <Component
      ref={ref}
      className={clsx(
        'ds-table-container',
        scrollX && 'ds-table-container--scroll',
        bordered && 'ds-table-container--bordered',
        elevated && 'ds-table-container--elevated',
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  );
});

const buildTableClassName = (density, striped, hoverable, className) =>
  clsx(
    'ds-table',
    density && `ds-table--density-${density}`,
    striped && 'ds-table--striped',
    hoverable && 'ds-table--hoverable',
    className,
  );

const renderEmptyState = (columnsLength, renderEmpty, emptyMessage) => {
  if (typeof renderEmpty === 'function') {
    const custom = renderEmpty();
    if (custom != null) return custom;
  }

  return (
    <TableRow>
      <TableCell
        align="center"
        className="ds-table__cell--empty"
        colSpan={Math.max(columnsLength, 1)}
      >
        {emptyMessage ?? '—'}
      </TableCell>
    </TableRow>
  );
};

const Table = React.forwardRef(function Table(props, ref) {
  const {
    children,
    className,
    density = 'comfortable',
    striped = false,
    hoverable = true,
    columns,
    dataSource,
    rowKey = 'id',
    pagination = null,
    emptyMessage = null,
    renderEmpty,
    onRow,
    caption = null,
    ...rest
  } = props;

  const tableClassName = buildTableClassName(density, striped, hoverable, className);
  const hasColumnMode = Array.isArray(columns) && columns.length > 0 && Array.isArray(dataSource);

  const captionContent = caption ?? null;

  if (!hasColumnMode) {
    return (
      <table ref={ref} className={tableClassName} {...rest}>
        {captionContent ? <TableCaption>{captionContent}</TableCaption> : null}
        {children}
      </table>
    );
  }

  const resolveRowKey =
    typeof rowKey === 'function'
      ? rowKey
      : (record, index) => {
          const value = record?.[rowKey];
          return value != null ? value : index;
        };

  const headerRow = (
    <TableRow>
      {columns.map((column, columnIndex) => {
        const headerKey = column.key ?? column.dataIndex ?? column.title ?? columnIndex;
        const headerProps = column.onHeaderCell ? column.onHeaderCell(column, columnIndex) || {} : {};
        const { className: headerClassNameProp, align: headerAlignProp, ...restHeaderProps } =
          headerProps;
        const headerAlign = headerAlignProp ?? column.align;
        const headerClassName = clsx(column.headerClassName, headerClassNameProp);
        return (
          <TableHeaderCell
            key={headerKey}
            align={headerAlign}
            className={headerClassName}
            {...restHeaderProps}
          >
            {typeof column.title === 'function' ? column.title(column, columnIndex) : column.title}
          </TableHeaderCell>
        );
      })}
    </TableRow>
  );

  const rows = dataSource.map((record, index) => {
    const key = resolveRowKey(record, index);
    const rowConfig = onRow ? onRow(record, index) || {} : {};
    const { className: rowClassName, clickable, selected, ...rowRest } = rowConfig;

    return (
      <TableRow
        key={key ?? index}
        className={rowClassName}
        clickable={Boolean(clickable)}
        selected={Boolean(selected)}
        {...rowRest}
      >
        {columns.map((column, columnIndex) => {
          const cellKey = column.key ?? column.dataIndex ?? `${columnIndex}-${key ?? index}`;
          const cellProps = column.onCell ? column.onCell(record, index) || {} : {};
          const { className: cellClassNameProp, align: cellAlignProp, ...restCellProps } = cellProps;
          const cellAlign = cellAlignProp ?? column.align;
          const cellClassName = clsx(column.className, cellClassNameProp);

          let content = null;
          if (typeof column.render === 'function') {
            const value = column.dataIndex ? record?.[column.dataIndex] : undefined;
            content = column.render(value, record, index);
          } else if (column.dataIndex) {
            content = record?.[column.dataIndex];
          }

          return (
            <TableCell
              key={cellKey}
              align={cellAlign}
              className={cellClassName}
              {...restCellProps}
            >
              {content}
            </TableCell>
          );
        })}
      </TableRow>
    );
  });

  const paginationContent =
    pagination && React.isValidElement(pagination) ? pagination : null;

  return (
    <table ref={ref} className={tableClassName} {...rest}>
      {captionContent ? <TableCaption>{captionContent}</TableCaption> : null}
      <TableHead>{headerRow}</TableHead>
      <TableBody>
        {rows.length > 0 ? rows : renderEmptyState(columns.length, renderEmpty, emptyMessage)}
      </TableBody>
      {paginationContent ? (
        <TableFooter>
          <TableRow>
            <TableCell
              className="ds-table__cell--pagination"
              colSpan={Math.max(columns.length, 1)}
            >
              {paginationContent}
            </TableCell>
          </TableRow>
        </TableFooter>
      ) : null}
    </table>
  );
});

Table.Head = TableHead;
Table.Body = TableBody;
Table.Footer = TableFooter;
Table.Row = TableRow;
Table.Cell = TableCell;
Table.HeaderCell = TableHeaderCell;
Table.Caption = TableCaption;
Table.Container = TableContainer;

export default Table;
export {
  TableHead,
  TableBody,
  TableFooter,
  TableRow,
  TableCell,
  TableHeaderCell,
  TableCaption,
  TableContainer,
};
